/**
 * AccountDetail header: company name, phase pills, phase strip,
 * header actions (Mark as On Hold / Mark as Lost / Resume).
 *
 * Client portals also render the lifecycle tracker (AccountLifecycleSection)
 * at the bottom of the header. The internal portal mounts it on the Overview
 * tab instead (mirrors cbs-portal), so the header skips it there.
 *
 * Manual lifecycle moves are owned by the parent's Move-group modal
 * (MoveLifecycleModal) — the band's "Move stage" button opens it via
 * `onEditLifecycle`.
 */

import React from 'react';
import {Link} from 'react-router-dom';
import {ArrowLeft, Briefcase, MapPin, Users} from 'lucide-react';
import {Button} from '@common-web/components/ui/button';
import {Badge} from '@common-web/components/ui/badge';
import type {
  LifecyclePhase,
  AccountsArchiveReason,
  AccountsSubStage,
  AccountsSwotStatus,
} from '@api/generated/client';
import {CODE_PERMISSIONS} from '@common/permissions';
import {useClientActionAllowed} from '@common-web/hooks/use-permissions';
import {useIsInternal} from '@common-web/providers/portalProvider';
import {
  CBS_LIVE_TILE,
  CBS_STAGE_PILL_CLASS,
  CBS_TENANT_BADGE_CLASS,
  cbsTenantTint,
} from '@common-web/components/account/cbsAccountsPresentation';
import {
  ARCHIVE_REASON_LABEL,
  SUB_STAGE_LABEL,
  SUB_STAGE_BADGE_VARIANT,
  toActiveSubStage,
} from '@common-web/components/account/phaseLabels';
import {useAccountsViewConfig} from '@common-web/hooks/useAccountsViewConfig';
import {findPositionBySystemSubStage, resolveAccountPosition} from '@common/accountsView';
import {type OpenEnrollmentMetadata} from '@common/lib/openEnrollment';
import type {GroupProcessType} from '@common/lib/groupProcessType';
import {OpenEnrollmentBadge} from '@common-web/components/account/OpenEnrollmentBadge';
import {AnalyzerGroupBadge} from '@common-web/components/account/AnalyzerGroupBadge';
import {AccountLifecycleSection, type SwotHandlers} from './AccountLifecycleSection';
import {StageTooltip} from './StageTooltip';
import type {NextActionHandlers} from './NextActionButton';
import {AssignedBrokerLink} from '@common-web/components/account/AssignedBrokerLink';

export type {SwotHandlers};

interface AccountHeaderProps {
  account: {
    id: string;
    companyName: string;
    lifecyclePhase: LifecyclePhase;
    subStage: AccountsSubStage;
    viewStageKey?: string | null;
    viewSubStageKey?: string | null;
    swotStatus: AccountsSwotStatus | null;
    swotScheduledDate: Date | string | null;
    isArchived: boolean;
    archiveReason?: AccountsArchiveReason | null;
    state: string | null;
    estimatedFtEmployees: number | null;
    assignedBroker: {id: string; name: string} | null;
    group: {
      id: string;
      estimatedMemberCount: number | null;
      vbaGroupId?: string | null;
      // Analyzer vs Monthly (from GroupMetadata.internalProcess) — drives the tag.
      groupProcessType?: GroupProcessType | null;
      // Open enrollment window (GroupMetadata) — drives the OE badge.
      metadata?: OpenEnrollmentMetadata | null;
    } | null;
    /** VBA Group_ID (from the linked group, or the live VBA group for virtual accounts). */
    vbaGroupId?: string | null;
    createdAt?: Date | string | null;
    stageTimeline?: {
      enteredAt: Record<string, string>;
      leftAt: Record<string, string>;
      skipped?: Record<string, {reason: string; at: string; by: string | null}>;
    } | null;
    stageDateOverrides?: Partial<Record<string, string>> | null;
    /** Tenant name — drives the internal header's TenantBadge pill. */
    client?: {name: string} | null;
  };
  routePrefix: string;
  hasRfc: boolean;
  /** Whether any MSA actually went out (envelope or manual record) — see NextActionButton. */
  msaSent: boolean;
  /** Resume / reopen an On Hold or Lost account back into the pipeline. */
  onUnarchive: () => void;
  onMarkOnHold: () => void;
  onMarkLost: () => void;
  /** Open the Move-group modal — the one surface for manual lifecycle moves. */
  onEditLifecycle: () => void;
  isTransitioning?: boolean;
  /** Advance the account forward one sub-stage (inline "Skip" in the band),
   *  recording the required reason against the skipped stage. */
  onSkipSubStage?: (targetSubStage: AccountsSubStage, reason: string) => void;
  /** Sub-stages the caller may manually move to (drives "Move stage"). */
  editableSubStages?: AccountsSubStage[];
  /** Handlers backing the contextual "Next action" button under the band. */
  nextActionHandlers: NextActionHandlers;
  /** Whether the broker-approval sub-stage is part of this tenant's pipeline. */
  brokerApprovalEnabled?: boolean;
  /** Whether this account's tenant has the "Welcome Call" implementation step
   *  (Synrgy/Quantum default-on, server-resolved on getById). */
  welcomeCallEnabled?: boolean;
  /** Edit a lifecycle step's date (internal analysts) — see LifecycleBand.
   *  `null` clears the step's date entirely. */
  onEditStageDate?: (subStage: AccountsSubStage, isoDate: string | null) => void;
  /** True when any next-action mutation is in flight. */
  isNextActionPending?: boolean;
  /** SWOT call (Synrgy-only). When enabled, the LEAD-phase next action becomes
   *  the SWOT request/schedule/complete flow instead of the proposal CTA. */
  swotEnabled?: boolean;
  swotHandlers?: SwotHandlers;
  /** Read-only virtual VBA account — hides the On Hold / Lost actions. */
  readOnly?: boolean;
}

function memberCount(account: AccountHeaderProps['account']): number | null {
  return account.group?.estimatedMemberCount ?? account.estimatedFtEmployees;
}

export function AccountHeader({
  account,
  routePrefix,
  hasRfc,
  msaSent,
  onUnarchive,
  onMarkOnHold,
  onMarkLost,
  onEditLifecycle,
  isTransitioning = false,
  onSkipSubStage,
  editableSubStages = [],
  nextActionHandlers,
  brokerApprovalEnabled = false,
  welcomeCallEnabled = false,
  onEditStageDate,
  isNextActionPending = false,
  swotEnabled = false,
  swotHandlers,
  readOnly = false,
}: AccountHeaderProps): React.ReactElement {
  const isInternal = useIsInternal();
  const members = memberCount(account);
  const vbaGroupId = account.group?.vbaGroupId ?? account.vbaGroupId ?? null;
  // Badge shows the milestone being worked toward — the same node the lifecycle
  // band below highlights — so the header never reads a step behind the band.
  // This mirrors the Accounts table badge (see AccountsListTable) exactly so the
  // detail header and the list row never disagree for the same account.
  const config = useAccountsViewConfig();
  const position = resolveAccountPosition(config, account);
  const activeSubStage = toActiveSubStage(account.subStage);
  const activePosition =
    activeSubStage === account.subStage
      ? position
      : findPositionBySystemSubStage(config, activeSubStage);
  // Don't advance the badge onto a milestone this tenant's pipeline omits
  // (e.g. a submitted RFC advancing to "Broker Approval" for a
  // client-only-approval tenant) — fall back to the account's own represented
  // sub-stage so the label matches the config (e.g. "RFC Submitted").
  const displaySubStage = activePosition ? activeSubStage : account.subStage;
  const displayPosition = activePosition ?? position;
  const displaySubStageLabel = displayPosition?.subStage.label ?? SUB_STAGE_LABEL[displaySubStage];

  // cbs-portal meta line: a colored day-in-stage dot (green under 3 days,
  // yellow through 7, red after). Dated from when the account entered its
  // current canonical sub-stage; hidden when no transition has been recorded.
  const enteredCurrentAt = account.stageTimeline?.enteredAt[account.subStage];
  const daysInStage =
    isInternal && enteredCurrentAt
      ? Math.max(0, Math.floor((Date.now() - new Date(enteredCurrentAt).getTime()) / 86_400_000))
      : null;
  const stageDotColor =
    daysInStage === null
      ? null
      : daysInStage < 3
        ? '#16A34A'
        : daysInStage <= 7
          ? '#EAB308'
          : '#DC2626';

  const canSetStatus = useClientActionAllowed(CODE_PERMISSIONS.accounts.archive.key);

  // cbs-portal "Stage N · sub-stage" meta fragment: the 1-based index of the
  // displayed stage within the active pipeline config.
  const stageNumber = displayPosition
    ? config.stages.findIndex((s) => s.key === displayPosition.stage.key) + 1
    : 0;

  return (
    // Internal portal renders the demo's flat-on-sand header (no card chrome);
    // client portals keep the muted card wrapper.
    <div
      className={
        isInternal ? 'px-1 py-1' : 'rounded-xl border border-border bg-muted/40 px-6 py-4 shadow-sm'
      }
    >
      <div className='flex items-center gap-2 mb-3'>
        <Link to={routePrefix}>
          <Button variant='ghost' size='sm'>
            <ArrowLeft className='w-4 h-4 mr-1' />
            Accounts
          </Button>
        </Link>
      </div>

      <div className='flex items-start justify-between gap-4'>
        <div className='flex-1 min-w-0'>
          <div className='flex flex-wrap items-center gap-x-3 gap-y-2'>
            <h1 className='truncate text-2xl font-bold'>{account.companyName}</h1>
            <div className='flex flex-wrap items-center gap-1.5'>
              {isInternal && account.client ? (
                <span className={CBS_TENANT_BADGE_CLASS} style={cbsTenantTint(account.client.name)}>
                  {account.client.name}
                </span>
              ) : null}
              {isInternal ? (
                // cbs-portal workflow status pill: green fill once live,
                // orange-soft while still in the pipeline.
                <span
                  className={CBS_STAGE_PILL_CLASS}
                  style={
                    account.lifecyclePhase === 'ACTIVE'
                      ? {backgroundColor: CBS_LIVE_TILE.bg, color: CBS_LIVE_TILE.text}
                      : {backgroundColor: '#fdeedd', color: '#d96f10'}
                  }
                >
                  {displaySubStageLabel}
                </span>
              ) : (
                <Badge variant={SUB_STAGE_BADGE_VARIANT[displaySubStage]}>
                  {displaySubStageLabel}
                </Badge>
              )}
              <StageTooltip
                phase={account.lifecyclePhase}
                subStage={account.subStage}
                viewStageKey={account.viewStageKey}
                viewSubStageKey={account.viewSubStageKey}
              />
              {account.isArchived ? (
                <Badge variant={account.archiveReason === 'ON_HOLD' ? 'secondary' : 'destructive'}>
                  {account.archiveReason ? ARCHIVE_REASON_LABEL[account.archiveReason] : 'Archived'}
                </Badge>
              ) : null}
              {/* Open enrollment is shown to every audience — broker, client admin, CBS */}
              <OpenEnrollmentBadge metadata={account.group?.metadata} />
              {/* Analyzer vs Monthly group — hidden when there is no linked
                  local Group row or its internalProcess is unclassified. */}
              <AnalyzerGroupBadge groupProcessType={account.group?.groupProcessType} />
            </div>
          </div>
          {members !== null ||
          account.assignedBroker ||
          account.state ||
          (isInternal && (vbaGroupId || daysInStage !== null || stageNumber > 0)) ? (
            <div className='mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground'>
              {isInternal && vbaGroupId ? (
                <span className='font-mono text-[12px] tabular-nums'>CBS Group #{vbaGroupId}</span>
              ) : null}
              {isInternal && stageNumber > 0 ? (
                <span>
                  Stage {stageNumber} · {displaySubStageLabel}
                </span>
              ) : null}
              {isInternal && daysInStage !== null && stageDotColor ? (
                <span className='inline-flex items-center gap-1.5'>
                  <span
                    aria-hidden
                    className='h-2 w-2 rounded-full'
                    style={{backgroundColor: stageDotColor}}
                  />
                  {daysInStage}d in stage
                </span>
              ) : null}
              {members !== null ? (
                <span className='inline-flex items-center gap-1.5'>
                  <Users className='h-4 w-4' />
                  {members} members
                </span>
              ) : null}
              {account.assignedBroker ? (
                <span className='inline-flex items-center gap-1.5'>
                  <Briefcase className='h-4 w-4' />
                  <AssignedBrokerLink broker={account.assignedBroker} />
                </span>
              ) : null}
              {account.state ? (
                <span className='inline-flex items-center gap-1.5'>
                  <MapPin className='h-4 w-4' />
                  {account.state}
                </span>
              ) : null}
            </div>
          ) : null}
        </div>

        <div className='flex shrink-0 items-center gap-2'>
          {/* On Hold / Lost (and resuming from them) share one permission key.
              Virtual VBA accounts are read-only — no status actions. */}
          {readOnly || !canSetStatus ? null : account.isArchived ? (
            <Button variant='outline' size='sm' onClick={onUnarchive}>
              {account.archiveReason === 'ON_HOLD' ? 'Resume' : 'Reopen'}
            </Button>
          ) : (
            <>
              <Button variant='outline' size='sm' onClick={onMarkOnHold}>
                Mark as On Hold
              </Button>
              <Button variant='outline' size='sm' onClick={onMarkLost}>
                Mark as Lost
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Internal portal mounts the lifecycle tracker on the Overview tab
          instead (mirrors cbs-portal), so the header skips it there. */}
      {isInternal ? null : (
        <div className='mt-5 border-t border-border pt-4'>
          <AccountLifecycleSection
            account={account}
            hasRfc={hasRfc}
            msaSent={msaSent}
            onEditLifecycle={onEditLifecycle}
            isTransitioning={isTransitioning}
            onSkipSubStage={onSkipSubStage}
            editableSubStages={editableSubStages}
            nextActionHandlers={nextActionHandlers}
            brokerApprovalEnabled={brokerApprovalEnabled}
            welcomeCallEnabled={welcomeCallEnabled}
            onEditStageDate={onEditStageDate}
            isNextActionPending={isNextActionPending}
            swotEnabled={swotEnabled}
            swotHandlers={swotHandlers}
          />
        </div>
      )}
    </div>
  );
}
